/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import * as React from 'react';
import RichText from 'apmui/components/markup/RichTextView';
import bind from 'apmui/decorators/bind';
import { IFormField, INPUT } from './Model';

/* declare attributes missing in @types/react */
declare module 'react' {
    /* eslint-disable @typescript-eslint/interface-name-prefix */
    interface HTMLAttributes<T> {
        autoCapitalize?: string;
    }
}
interface IInputExtraProps {
    onChange?: (name: string, value: string | boolean, event: Event) => void;
}

class Input extends React.Component<IFormField & IInputExtraProps> {
    @bind
    onChange(evt: Event | React.SyntheticEvent) {
        const target = evt.target as HTMLInputElement;
        const value = this.props.type === 'checkbox' ? target.checked : target.value;
        if (this.props.onChange) {
            this.props.onChange(this.props.name, value, evt as Event);
        }
    }

    text(str: string) {
        return this.props.richText ? <RichText text={str} /> : str;
    }

    render() {
        const props = this.props;
        const changeHandler = this.props.onChange ? this.onChange : undefined;
        const classes = ['form-input-container', 'form-input-' + props.type];
        const containerId = `${props.name}-container`;
        if (props.error) {
            classes.push('form-input-invalid');
        }
        let inputField: any = '';

        const errorMessage = props.error ? <p className="input-error-message">{props.error}</p> : null;

        switch (props.type) {
            case INPUT.SELECT:
                inputField = (
                    /* eslint-disable-next-line jsx-a11y/no-onchange */
                    <select id={props.name} name={props.name} disabled={props.disabled} onChange={changeHandler}>
                        {props.options.map((entry) => (
                            <option key={entry.value} value={entry.value} selected={entry.value === props.value}>
                                {entry.text || entry.value}
                            </option>
                        ))}
                    </select>
                );
                break;
            case INPUT.RADIO:
                inputField = props.options.map((entry, idx) => (
                    <p key={entry.value} className="radio-input-container">
                        <input
                            id={props.name + '' + idx}
                            name={props.name}
                            type="radio"
                            value={entry.value}
                            disabled={props.disabled}
                            checked={entry.value === props.value}
                            onChange={changeHandler}
                        />
                        <label htmlFor={props.name + '' + idx}>{this.text(entry.text || entry.value)}</label>
                    </p>
                ));
                return (
                    <fieldset className={classes.join(' ')} id={containerId}>
                        <legend>{this.text(props.caption)}</legend>
                        {inputField}
                        {errorMessage}
                    </fieldset>
                );
            case INPUT.CHECKBOX: {
                const checkboxValue = props.default || '1';
                return (
                    <div className={classes.join(' ')} id={containerId}>
                        <input
                            id={props.name}
                            name={props.name}
                            type={props.type}
                            autoComplete="off"
                            autoCapitalize="off"
                            disabled={props.disabled}
                            value={checkboxValue}
                            checked={props.value}
                            onChange={changeHandler}
                        />
                        <label htmlFor={props.name}>{this.text(props.caption)}</label>
                        {errorMessage}
                    </div>
                );
            }
            case INPUT.HIDDEN:
            case INPUT.NONE:
                // immediate return
                return null;
            default:
                inputField = (
                    <input
                        id={props.name}
                        name={props.name}
                        type={props.type}
                        autoComplete="off"
                        autoCapitalize="off"
                        disabled={props.disabled}
                        value={props.value}
                        placeholder={props.placeholder}
                        onInput={changeHandler}
                    />
                );
                break;
        }

        return (
            <div className={classes.join(' ')} id={containerId}>
                {props.caption && <label htmlFor={props.name}>{this.text(props.caption)}</label>}
                {inputField}
                {errorMessage}
            </div>
        );
    }
}

export default Input;
